import { FC, RefObject, useState } from "react";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import { formatCurrencyFromCents } from "@simplyk/common";
import { useFormContext, useWatch } from "react-hook-form";

import { MAX_PRIZE_TITLE_LENGTH } from "../helpers/prizeFormData";
import { StepPrizesFormData } from "../helpers/types";

import { PrizeImageField } from "./PrizeImageField/PrizeImageField";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { isRequired } from "@/common/helpers/validators";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DeleteConfirmations } from "@/components/DeleteConfirmation/DeleteConfirmation";
import { ImageUploaderHandle } from "@/components/ImageUploader/types";
import { DashboardAmountInput } from "@/dashboard-design-system/DashboardAmountInput";
import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTooltip } from "@/dashboard-design-system/DashboardTooltip";
import { DashboardRichTextField } from "@/dashboard-design-system/RichTextField";
import { DashboardTextField } from "@/dashboard-design-system/TextField/DashboardTextField";
import { containsRaffleWord } from "@/features/LiveFormEditor/common/sweepstakes";
import { ThumbnailedSummaryLayout } from "@/features/LiveFormEditor/Steps/StepTickets/Rates/Rate/shared/RateIdleHeader/ThumbnailedSummaryLayout";
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronUp,
  Copy as DuplicateIcon,
  Hide,
  Move as DragIcon,
  Reveal,
} from "@/icons/outlined";

interface PrizeCardProps {
  index: number;
  prizeId: string;
  totalCardNumber: number;
  isExpanded: boolean;
  onToggleExpanded: (prizeId: string) => void;
  onDelete: (index: number) => void;
  onDuplicate: (index: number) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  onUploaderChange?: (prizeId: string, uploaderRef: RefObject<ImageUploaderHandle | null> | null) => void;
  onActivate?: (prizeId: string) => void;
  isSweepstakes?: boolean;
}

export const PrizeCard: FC<PrizeCardProps> = ({
  index,
  prizeId,
  totalCardNumber,
  isExpanded,
  onToggleExpanded,
  onDelete,
  onDuplicate,
  onMoveUp,
  onMoveDown,
  onUploaderChange,
  onActivate,
  isSweepstakes = false,
}) => {
  const { t } = useTranslate();
  const { isoLocale, locale } = useLocaleContext();
  const { organizationCurrency } = useCurrentUserContext();
  const { isSmallScreen } = useMediaQuery();
  const { control, setValue, getValues, formState } = useFormContext<StepPrizesFormData>();
  const prize = useWatch({ control, name: `prizes.${index}` });
  const [initialDescription] = useState(() => getValues(`prizes.${index}.description`) ?? "");
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: prizeId });

  const hasMultipleCards = totalCardNumber > 1;
  const canDrag = !isSmallScreen;
  const showMobileArrows = hasMultipleCards && isSmallScreen;
  const titleError = formState.errors.prizes?.[index]?.title;
  const descriptionError = formState.errors.prizes?.[index]?.description;
  const titleRules = [
    isRequired(t("dashboard", "liveFormEditor.stepPrizes.prizeTitleRequired") as string),
    ...(isSweepstakes ? [rejectRaffleWord(t("dashboard", "sweepstakes.validation.raffleWord.title"))] : []),
  ];
  const descriptionRules = isSweepstakes
    ? [rejectRaffleWord(t("dashboard", "sweepstakes.validation.raffleWord.description"))]
    : undefined;

  const cardTitle = `${index + 1}. ${prize?.title?.trim() || t("dashboard", "liveFormEditor.stepPrizes.fallbackTitle")}`;
  const valueSummary =
    prize?.value !== null && prize?.value !== undefined
      ? t("dashboard", "liveFormEditor.stepPrizes.valueSummary", {
          value: formatCurrencyFromCents(prize.value, { isoLocale, currency: organizationCurrency }),
        })
      : null;
  const shouldConfirmDelete = Boolean(prize?.title?.trim() || prize?.description || prize?.photoUrl || prize?.value);

  const toggleExpanded = () => onToggleExpanded(prizeId);

  return (
    <Box
      ref={setNodeRef}
      data-index={index}
      data-test="prize-card"
      {...attributes}
      onClickCapture={() => onActivate?.(prizeId)}
      onFocusCapture={() => onActivate?.(prizeId)}
      sx={{
        transform: CSS.Translate.toString(transform),
        transition,
        boxShadow: isDragging ? "0 4px 8px rgba(0,0,0,0.2)" : undefined,
        zIndex: isDragging ? 1000 : undefined,
        position: "relative",
      }}
    >
      <Paper
        sx={(theme) => ({
          paddingY: 2.5,
          paddingX: 3,
          backgroundColor: theme.palette.surface.neutral.supershy,
          borderRadius: theme.radius(1),
          boxShadow: "none",
        })}
      >
        <Stack spacing={isExpanded ? 1 : 0}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Stack direction="row" alignItems="center" spacing={isExpanded ? 2 : 1.5} sx={{ flex: 1, minWidth: 0 }}>
              {canDrag && (
                <Box
                  {...listeners}
                  sx={{ display: "flex", flexShrink: 0, cursor: "grab", "&:active": { cursor: "grabbing" } }}
                >
                  <DashboardIconButton vibe="neutral" size="small" sx={{ cursor: "inherit" }}>
                    <DragIcon />
                  </DashboardIconButton>
                </Box>
              )}
              <Box
                onClick={toggleExpanded}
                sx={{ display: "flex", flex: 1, minWidth: 0, cursor: "pointer" }}
                data-test="prize-card-header"
              >
                <ThumbnailedSummaryLayout
                  title={cardTitle}
                  thumbnailUrl={isExpanded ? null : (prize?.photoUrl ?? null)}
                  subtitle={valueSummary}
                  isExpanded={isExpanded}
                />
              </Box>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
              {showMobileArrows && (
                <>
                  <DashboardIconButton
                    size="small"
                    vibe="neutral"
                    onClick={() => onMoveUp(index)}
                    disabled={index === 0}
                    data-test="prize-move-up-button"
                  >
                    <ArrowUp />
                  </DashboardIconButton>
                  <DashboardIconButton
                    size="small"
                    vibe="neutral"
                    onClick={() => onMoveDown(index)}
                    disabled={index === totalCardNumber - 1}
                    data-test="prize-move-down-button"
                  >
                    <ArrowDown />
                  </DashboardIconButton>
                </>
              )}

              {isExpanded && (
                <>
                  <DashboardTooltip
                    title={t(
                      "dashboard",
                      prize?.isHidden ? "liveFormEditor.stepPrizes.show" : "liveFormEditor.stepPrizes.hide"
                    )}
                  >
                    <DashboardIconButton
                      size="small"
                      vibe="neutral"
                      onClick={() => setValue(`prizes.${index}.isHidden`, !prize?.isHidden, { shouldDirty: true })}
                      data-test="prize-visibility-toggle"
                    >
                      {prize?.isHidden ? <Hide /> : <Reveal />}
                    </DashboardIconButton>
                  </DashboardTooltip>
                  <DashboardTooltip title={t("dashboard", "liveFormEditor.stepPrizes.duplicate")}>
                    <DashboardIconButton
                      size="small"
                      vibe="neutral"
                      onClick={() => onDuplicate(index)}
                      data-test="prize-duplicate-button"
                    >
                      <DuplicateIcon />
                    </DashboardIconButton>
                  </DashboardTooltip>
                  <DashboardTooltip title={t("dashboard", "liveFormEditor.stepPrizes.delete")}>
                    <Box sx={{ flexShrink: 0 }}>
                      <DeleteConfirmations confirmation={() => onDelete(index)} shouldConfirm={shouldConfirmDelete} />
                    </Box>
                  </DashboardTooltip>
                </>
              )}

              <DashboardIconButton
                size="small"
                vibe="neutral"
                onClick={toggleExpanded}
                title={t("common", isExpanded ? "collapse" : "expand")}
                data-test="prize-expand-toggle"
              >
                {isExpanded ? <ChevronUp /> : <ChevronDown />}
              </DashboardIconButton>
            </Stack>
          </Stack>

          <Collapse in={isExpanded} timeout="auto" unmountOnExit={false}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
              <PrizeImageField index={index} prizeId={prizeId} onUploaderChange={onUploaderChange} />
              <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
                <DashboardTextField
                  data-test="prize-title-input"
                  debounced={250}
                  name={`prizes.${index}.title`}
                  label={t("dashboard", "liveFormEditor.stepPrizes.prizeTitle")}
                  control={control}
                  errorMessage={titleError?.message}
                  rules={titleRules}
                  inputProps={{ maxLength: MAX_PRIZE_TITLE_LENGTH }}
                />
                <DashboardAmountInput
                  data-test="prize-value-input"
                  control={control}
                  name={`prizes.${index}.value`}
                  label={t("dashboard", "liveFormEditor.stepPrizes.value")}
                  locale={locale}
                  currency={organizationCurrency}
                />
                <DashboardRichTextField
                  data-test="prize-description-input"
                  debounced={250}
                  defaultValue={initialDescription}
                  name={`prizes.${index}.description`}
                  label={t("dashboard", "ticketing.rateDescription")}
                  control={control}
                  rules={descriptionRules}
                  errorMessage={descriptionError?.message}
                  placeholder={t("dashboard", "liveFormEditor.stepPrizes.descriptionPlaceholder")}
                  slotProps={{
                    placeholder: {
                      sx: (theme) => ({
                        right: theme.spacing(1),
                        bottom: theme.spacing(1),
                        overflow: "hidden",
                        display: "-webkit-box",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: 3,
                      }),
                    },
                  }}
                />
              </Stack>
            </Stack>
          </Collapse>
        </Stack>
      </Paper>
    </Box>
  );
};

const rejectRaffleWord = (message: string) => ({
  validate: (value: string | null | undefined) => !containsRaffleWord(value) || message,
});
